import {
  Button,
  Col,
  Divider,
  Flex,
  Input,
  message,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import AZPopConfirm from "components/PopConfirm";
import {
  updateUserInfo,
  resetPasswordUser,
  revertPasswordUser,
  removeUserMFA,
  resetUserLoginSecurity,
  getUserLoginSecurityStatus,
} from "services";
import { convertDateTime } from "utils";

const formatFieldName: any = {
  firstName: "First name",
  lastName: "Last name",
  password: "Password (hashed)",
  verifyPhone: "Verify for Limited trial",
  lastLoginAt: "AZInsight Last Login At",
  azinsightVersion: "AZInsight Version",
  mobileLastTimeLoginVersion: "ScanEZ Version",
  mobileLastTimeLogin: "ScanEZ Login",
};

const editableFields = ["firstName", "lastName"];
const customValueFields = ["verifyPhone"];
const convertTimeFields = ["lastLoginAt", "mobileLastTimeLogin"];

const ProfileTab = ({ data, onReFetchUser }: any) => {
  const [dataSource, setDataSource] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingField, setLoadingField] = useState<boolean>(false);
  const [fieldUpdate, setFieldUpdate] = useState<string>("");
  const [noteUpdate, setNoteUpdate] = useState<string>("");
  const [mfaStatus, setMfaStatus] = useState<boolean>(false);
  const [loginSecurityStatus, setLoginSecurityStatus] = useState<any>(null);
  const [loadingMFA, setLoadingMFA] = useState<boolean>(false);
  const [loadingSecurity, setLoadingSecurity] = useState<boolean>(false);

  const userId = data.user._id;
  const originPassword = data.origin_password;

  const renderPasswordChangeButtons = (
    field: string,
    id: string,
    temp: boolean
  ) => (
    <Space size="large">
      {temp ? (
        <AZPopConfirm
          placement="topRight"
          confirmText="Password is already reset to default, this action will revert it back to original password"
          buttonText="Revert"
          danger={false}
          onConfirm={() => handleOnRevertPassword(field, id)}
        />
      ) : (
        <AZPopConfirm
          placement="topRight"
          confirmText="This cannot be undone, would you like to continue?"
          buttonText="Password Temp change"
          onConfirm={() => handleOnResetPassword(field, id, true)}
        />
      )}
      <AZPopConfirm
        placement="topRight"
        confirmText="This cannot be undone, would you like to continue?"
        buttonText="Password change (no revert)"
        onConfirm={() => handleOnResetPassword(field, id, false)}
      />
    </Space>
  );

  const handleOnChangeTextInline = (text: string, field: string) => {
    setFieldUpdate(field);
    setLoadingField(true);

    const params = {
      value: text,
      field,
      id: userId,
    };
    updateUserInfo(params)
      .then(() => {
        message.success("Saved successfully");
        setDataSource((prevState: any) => {
          onReFetchUser(true);
          const newData = prevState.map((item: any) => {
            if (item.key === field) {
              return {
                ...item,
                value: text,
              };
            }
            return item;
          });
          setLoadingField(false);
          return newData;
        });
      })
      .catch(() => {
        message.error("Failed to save");
        setLoadingField(false);
      });
  };

  const handleOnSelectField = (value: any, field: string) => {
    const valueUpdate = value ? "1" : "0";
    const params = {
      value: valueUpdate,
      field,
      id: userId,
    };
    updateUserInfo(params)
      .then(() => {
        setDataSource((prevState: any) => {
          onReFetchUser(true);
          const newData = prevState.map((item: any) => {
            if (item.key === field) {
              return {
                ...item,
                value: (
                  <Select
                    loading={fieldUpdate === field && loadingField}
                    onSelect={(value: any) => handleOnSelectField(value, field)}
                    value={dataSource[field]}
                    options={[
                      { label: "True", value: true },
                      { label: "False", value: false },
                    ]}
                  />
                ),
              };
            }
            return item;
          });
          return newData;
        });
        message.success("Saved successfully");
      })
      .catch(() => {
        setLoadingField(false);
        message.error("Couldn't save successfully");
      });
  };

  const handleOnResetPassword = (
    field: string,
    id: string,
    temp: boolean = true
  ) => {
    setFieldUpdate(field);
    setLoadingField(true);

    const params = { temp };
    resetPasswordUser(id, { params })
      .then(({ data: res }) => {
        onReFetchUser(true);
        setDataSource((prevState: any) => {
          const newData = prevState.map((item: any) => {
            if (item.key === field) {
              return {
                ...item,
                value: res.data,
                buttons: renderPasswordChangeButtons(field, id, temp),
              };
            }
            return item;
          });
          setLoadingField(false);
          return newData;
        });

        message.success("Reset password successfully");
      })
      .catch(() => {
        setLoadingField(false);
        message.error("Cannot reset password");
      });
  };

  const handleOnRevertPassword = (field: string, id: string) => {
    setFieldUpdate(field);
    setLoadingField(true);

    revertPasswordUser(id)
      .then(({ data: res }) => {
        setDataSource((prevState: any) => {
          onReFetchUser(true);
          const newData = prevState.map((item: any) => {
            if (item.key === field) {
              return {
                ...item,
                value: res.data,
                buttons: renderPasswordChangeButtons(field, id, false),
              };
            }
            return item;
          });
          setLoadingField(false);
          return newData;
        });

        message.success("Reverted password successfully");
      })
      .catch(() => {
        setLoadingField(false);
        message.error("Cannot reverted password");
      });
  };

  const formatData = (source: any) => {
    setLoading(true);
    let profileData: any = [];

    //format for fields
    Object.keys(formatFieldName).forEach((field: string) => {
      const isEditable = editableFields.includes(field) ? true : false;
      const valueItem = customValueFields.includes(field) ? (
        <Select
          loading={fieldUpdate === field && loadingField}
          defaultValue={source[field]}
          onSelect={(value: any) => handleOnSelectField(value, field)}
          options={[
            { label: "True", value: true },
            { label: "False", value: false },
          ]}
        />
      ) : (
        source[field]
      );
      let item: any = {
        key: field,
        field: formatFieldName[field],
        value: valueItem || "None",
        editable: isEditable
          ? {
            onChange: (text: string) => handleOnChangeTextInline(text, field),
          }
          : false,
      };

      if (field === "password") {
        const id = source._id;
        item.buttons = renderPasswordChangeButtons(
          field,
          id,
          originPassword[0]?.useDefaultPassword
        );
      }

      if (convertTimeFields.includes(field)) {
        item.value = convertDateTime(valueItem);
      }

      profileData.push(item);
    });

    setDataSource(() => {
      setLoading(false);
      return profileData;
    });
  };

  useEffect(() => {
    setNoteUpdate(data.user.note);
    formatData(data.user);
    // Load MFA status
    setMfaStatus(data.user.totpEnabled || false);
    // Load login security status
    loadLoginSecurityStatus();
  }, [userId]);

  const loadLoginSecurityStatus = async () => {
    try {
      const response = await getUserLoginSecurityStatus(userId);
      setLoginSecurityStatus(response.data.data);
    } catch (error) {
      console.error('Failed to load login security status:', error);
    }
  };

  const handleRemoveMFA = async () => {
    setLoadingMFA(true);
    try {
      await removeUserMFA(userId);
      setMfaStatus(false);
      message.success("MFA removed successfully");
      onReFetchUser(true);
    } catch (error: any) {
      message.error("Failed to remove MFA");
      console.error(error);
    } finally {
      setLoadingMFA(false);
    }
  };

  const handleResetLoginSecurity = async () => {
    setLoadingSecurity(true);
    try {
      await resetUserLoginSecurity(userId);
      await loadLoginSecurityStatus();
      message.success("Login security reset successfully");
      onReFetchUser(true);
    } catch (error: any) {
      message.error("Failed to reset login security");
      console.error(error);
    } finally {
      setLoadingSecurity(false);
    }
  };

  return (
    <>
      {loading ? (
        <div className="loading-page">
          <Spin size="large" />
        </div>
      ) : (
        <Flex gap={3}>
          <Space
            direction="vertical"
            size="large"
            style={{ width: "100%", padding: 20 }}
          >
            <Row gutter={[16, 16]} key={`row-${data.field}`}>
              <Col span={4}>
                <Typography.Text strong>Notes:</Typography.Text>
              </Col>
              <Col span={12}>
                <Input.TextArea
                  style={{ marginBottom: 5 }}
                  value={noteUpdate}
                  onChange={(e) => setNoteUpdate(e.target.value)}
                />
                <Button
                  type="primary"
                  loading={fieldUpdate === "note" && loadingField}
                  onClick={() => handleOnChangeTextInline(noteUpdate, "note")}
                >
                  Add Note
                </Button>
              </Col>
            </Row>
            {dataSource.length > 0 &&
              dataSource.map((data: any) => (
                <Row gutter={[16, 16]} key={`row-${data.field}`}>
                  <Col span={4}>
                    <Typography.Text strong>{data.field}:</Typography.Text>
                  </Col>
                  {data.buttons ? (
                    <Col>
                      <Space direction="vertical">
                        {fieldUpdate === data.key && loadingField ? (
                          <Spin
                            indicator={
                              <LoadingOutlined style={{ fontSize: 24 }} spin />
                            }
                          />
                        ) : (
                          <>
                            {data.value}
                            {data.buttons}
                          </>
                        )}
                      </Space>
                    </Col>
                  ) : (
                    <Col>
                      {fieldUpdate === data.key && loadingField ? (
                        <Spin
                          indicator={
                            <LoadingOutlined style={{ fontSize: 24 }} spin />
                          }
                        />
                      ) : (
                        <Typography.Text editable={data.editable}>
                          {data.value}
                        </Typography.Text>
                      )}
                    </Col>
                  )}
                </Row>
              ))}

            {/* 2FA / MFA Status */}
            <Row gutter={[16, 16]}>
              <Col span={4}>
                <Typography.Text strong>2FA / MFA Status:</Typography.Text>
              </Col>
              <Col>
                <Space>
                  <Tag color={mfaStatus ? "green" : "red"}>
                    {mfaStatus ? "Enabled" : "Not Enabled"}
                  </Tag>
                  {mfaStatus && (
                    <Popconfirm
                      title="Remove MFA"
                      description="This will remove all MFA settings for this user. They will need to set up MFA again. Continue?"
                      onConfirm={handleRemoveMFA}
                      okText="Yes, Remove"
                      cancelText="Cancel"
                      okButtonProps={{ danger: true }}
                    >
                      <Button
                        type="primary"
                        danger
                        loading={loadingMFA}
                        size="small"
                      >
                        Remove MFA
                      </Button>
                    </Popconfirm>
                  )}
                </Space>
              </Col>
            </Row>

            {/* Login Security Status */}
            <Row gutter={[16, 16]}>
              <Col span={4}>
                <Typography.Text strong>Login Security Status:</Typography.Text>
              </Col>
              <Col>
                <Space direction="vertical" size="small">
                  {loginSecurityStatus ? (
                    <>
                      {loginSecurityStatus.isLocked ? (
                        <Typography.Text type="danger">
                          Account is BLOCKED
                        </Typography.Text>
                      ) : loginSecurityStatus.isUnderCooldown ? (
                        <Typography.Text type="warning">
                          Under cooldown - {loginSecurityStatus.cooldownRemaining} remaining
                        </Typography.Text>
                      ) : (
                        <Typography.Text type="success">
                          Account is OK
                        </Typography.Text>
                      )}
                      {loginSecurityStatus.failedLoginAttempts > 0 && (
                        <Typography.Text type="secondary">
                          Failed login attempts: {loginSecurityStatus.failedLoginAttempts}
                        </Typography.Text>
                      )}
                    </>
                  ) : (
                    <Spin size="small" />
                  )}
                  <Popconfirm
                    title="Reset Login Counters & Unlock"
                    description="This will reset failed login attempts, unlock the account, and clear cooldown. Continue?"
                    onConfirm={handleResetLoginSecurity}
                    okText="Yes, Reset"
                    cancelText="Cancel"
                    okButtonProps={{ danger: true }}
                  >
                    <Button
                      type="primary"
                      loading={loadingSecurity}
                      size="small"
                    >
                      Reset Login Counters & Unlock
                    </Button>
                  </Popconfirm>
                </Space>
              </Col>
            </Row>
          </Space>
        </Flex>
      )}
    </>
  );
};

export default ProfileTab;
